import React, { useEffect, useState } from 'react';
import {
    DefaultButton,
    Icon,
    ImageIcon,
    Link,
    Text,
    LinkBase,
} from '@fluentui/react';
import './DetailsPanel.scss';
import { usePostRequestType, useRemoveCustomerImpact } from 'app/services/mutations';
import { formatCustomerSummary, NO_CUSTOMER_PLACEHOLDER } from 'app/modules/newTicketView/customerDisplay';
import {
    GENERAL_FEEDBACK_CUSTOMER_LABEL,
    isGeneralFeedbackTicket,
    resolveCustomerSummaryText,
} from 'app/modules/newTicketView/generalFeedback';
import { ViewerRole } from 'app/modules/newTicketView/types';
import { useAddBookmark, useRemoveBookmark, useAssociateRequests } from 'app/services/request-api';
import { EnumLookUp, IAssociateRequestsDto, IRequestDto, RequestDto, UserDto } from 'generated/request-backend';
import ShareRequestModal from 'app/components/shareRequestModal/ShareRequestModal';
import {
    ISOToDateTime,
    GetText,
    formatCurrency,
    GetTypes,
    keyPress,
    ReplaceUrlByAnchor,
    highlightTags,
    changeDateFormat,
    requestAggregateSubmit,
    injectTargetToAnchor,
    getURLForTeamAndType
} from 'app/components/helpers';
import AgingProgress from 'app/components/agingProgress/AgingProgress';
import CommentSection from 'app/components/commentSection/CommentSection';
import { Trans } from 'react-i18next';
import attachmentActiveIcon from 'app/static/icons/attachment-blue-icon.svg';
import AddImpact from 'app/components/addCustomer/AddCustomer';
import AddCustomerSurface from 'app/components/addCustomer/AddCustomerSurface';
import AddCustomerSuccessToast from 'app/components/addCustomer/AddCustomerSuccessToast/AddCustomerSuccessToast';
import { resolveCustomerImpactSuccess } from 'app/components/addCustomer/customerImpactSuccess';
import RemoveCustomerDialog from 'app/components/removeCustomerDialog/RemoveCustomerDialog';
import { useBoolean } from '@fluentui/react-hooks';
import useStore, { IStore } from 'app/store';
import AddAttachmentModal from 'app/components/addAttachmentModal/AddAttachmentModal';
import { IAttachmentUpdateData, ICommentCriteria, IRemoveCustomerImpactData } from 'app/models/common/request';
import { Loader } from 'app/components/loader/Loader';
import { useBlobFileSasTokens, useFetchStatus } from 'app/services/request-api';
import _ from 'lodash';
import { AppQueryClient } from 'app/services/clients/query-client';
import { attachmentCounter, getPortalValue } from 'app/utils/utilities';
import { useHistory } from 'react-router-dom';
import { RequestStatus } from 'app/enums/Status.enum';
import { BlobStorageContainer, Entity, StringConstants } from 'app/utils/constants';
import { addBlobFileSasToken } from 'app/utils/blobStorageHelpers';
import { CustomerImpactDto, CustomerImpactResponseDto } from 'generated/cxp-backend';
import DOMPurify from 'dompurify';
import { UserDomain } from 'app/enums/UserDomain.enum';
import CustomerDetailsTab from 'app/modules/admin/detailsPanel/customerDetailsTab/CustomerDetailsTab';
import ConfidentialIdentifier from 'app/modules/admin/detailsPanel/requestDetailsTab/ConfidentialIdentifier';
import ContextualDetailsCard from 'app/modules/admin/detailsPanel/requestDetailsTab/ContextualDetailsCard';
import FeedbackSourceCard from 'app/modules/admin/detailsPanel/requestDetailsTab/FeedbackSourceCard';
import GeneralFeedbackEmptyState from 'app/modules/admin/detailsPanel/requestDetailsTab/GeneralFeedbackEmptyState';
import DetailCard from 'app/modules/admin/detailsPanel/requestDetailsTab/DetailCard';
import { ICustomerImpactDto } from 'generated/crew-backend';
import { logFlowEvent } from 'app/modules/user/createTicket/flowTelemetry';
import { ViewTelemetryConstants, VIEW_TELEMETRY_SECTION } from 'app/modules/newTicketView/telemetryConstants';
import { useIsNewTicketViewEnabled } from 'app/modules/newTicketView/viewFlag';

interface IProps {
    requestData?: RequestDto[];
    selectedRequest?: RequestDto;
    requestTypes?: EnumLookUp[];
    statusTypes?: EnumLookUp[];
    setIsAddCustomerModalOpen?: Function;
    cardTypeFilter?: number;
    setIsBookmarked?: Function;
    isBookMarked?: boolean;
    resetData?: Function;
    teams?: EnumLookUp[];
    refetchPageData?: Function;
    isLoadingRequestList?: boolean;
    setSelectedRequest: Function;
    pageType?: string;
}

const DetailsPanel: React.FC<IProps> = (props) => {
    // props variables
    const { selectedRequest, requestTypes, statusTypes, teams, isLoadingRequestList, requestData, setSelectedRequest } = props;
    // props function
    const { refetchPageData, pageType } = props;

    // state variables
    const portal = getPortalValue(useHistory()?.location?.pathname);
    const addSuccessPopUp = useStore((state: IStore) => state.addSuccessPopUp);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const [isShareRequestModalOpen, setIsShareRequestModalOpen] = useState(false);
    const [isStarred, setIsStarred] = useState(false);
    const [isAddAttachmentModalOpen, setIsAddAttachmentModalOpen] = useState(false);
    const [isShowComment, setIsShowComment] = useState(true);
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails) as UserDto;
    const isNewTicketViewEnabled = useIsNewTicketViewEnabled();
    const isRequestCreator =
        (!!userDetails.id && userDetails.id === selectedRequest?.createdBy?.id)
        || (!!userDetails.email
            && !!selectedRequest?.createdBy?.email
            && userDetails.email.toLowerCase() === selectedRequest.createdBy.email.toLowerCase());
    const { mutate: addBookmark } = useAddBookmark();
    const { mutate: removeBookmark } = useRemoveBookmark();
    const [attachmentCount, setAttachmentCount] = useState<number>(0);
    const isClosed = selectedRequest?.status === RequestStatus.Closed;
    const showConfidentialIdentifier =
        isNewTicketViewEnabled && isRequestCreator && !!selectedRequest?.confidentialIdentifier?.trim();
    // General Feedback tickets have no customer, so customer-derived UI is replaced.
    const isGeneralFeedback = isNewTicketViewEnabled && isGeneralFeedbackTicket(selectedRequest);
    const viewerRole = isRequestCreator ? ViewerRole.Creator : ViewerRole.Viewer;

    const [criteria, setCriteria] = useState<ICommentCriteria>({});
    const { data: commentsData, isLoading: isLoadingComments } = useFetchStatus(criteria);

    const handleShareRequest = () => {
        setIsShareRequestModalOpen(true);
    };

    const handleAddAttachment = () => {
        setIsAddAttachmentModalOpen(true);
    };

    const getModalData = (data: RequestDto) => {
        let attachmentData: IAttachmentUpdateData = {};
        attachmentData.applicationId = data.backendId === null ? '--' : data.backendId;
        attachmentData.attachments = data.attachments;
        attachmentData.requestId = data.unifiedTrackingId;
        // attachmentData.requestType = GetText(requestTypes, data.requestType);
        // attachmentData.teamName = GetText(teams, data.requestTeam);
        attachmentData.requestType = requestTypes.find((element: EnumLookUp) => element.id === data.requestType)?.name;
        attachmentData.teamName = UserDomain[data.requestTeam as number];
        attachmentData.teamValue = data.requestTeam;
        attachmentData.requestTypeValue = data.requestType;
        attachmentData.summary = data.summary;
        return attachmentData;
    };

    const renderTag = (status: number) => {
        let label = '';
        let tagClass = '';
        switch (status) {
            case 1:
                label = GetText(statusTypes, status);
                tagClass = 'tag-type-a';
                break;
            case 5:
                label = GetText(statusTypes, status);
                tagClass = 'tag-type-b';
                break;
            case 2:
                label = GetText(statusTypes, status);
                tagClass = 'tag-type-c';
                break;
            default:
                label = GetText(statusTypes, status);
                tagClass = 'tag-type-a';
                break;
        }
        return <Text className={`quick-tag ${tagClass}`}>{label}</Text>;
    };

    const onToggleBookmark = () => {
        if (isStarred) {
            removeBookmark(selectedRequest.unifiedTrackingId);
        } else {
            addBookmark(selectedRequest.unifiedTrackingId);
        }
        setTimeout(() => {
            if (pageType === StringConstants.DETAILS_PAGE) {
                AppQueryClient.invalidateQueries('getRequestById');
            } else {
                refetchPageData();
            }
        }, 500);
    };

    const handleAttachmentCompleted = () => {
        if (pageType === StringConstants.DETAILS_PAGE) {
            AppQueryClient.invalidateQueries('getRequestById');
        } else {
            refetchPageData();
        }
    };


    useEffect(() => {
        setIsStarred(selectedRequest?.isBookmarked);
    }, [selectedRequest?.isBookmarked]);

    useEffect(() => {
        if (showConfidentialIdentifier) {
            logFlowEvent(
                ViewTelemetryConstants.CONFIDENTIAL_IDENTIFIER_VIEWED,
                VIEW_TELEMETRY_SECTION,
                {
                    role: 'creator',
                    requestType: selectedRequest.requestType
                }
            );
        }
    }, [selectedRequest?.requestType, showConfidentialIdentifier]);

    useEffect(() => {
        setIsShowComment(true);
    }, [selectedRequest]);

    useEffect(() => {
        // set filter for fetching status information
        if (selectedRequest) {
            let filterData: ICommentCriteria = {
                correlationId: selectedRequest.unifiedTrackingId,
                pageNumber: 1,
                pageSize: 1,
                entityType: 1,
                isInternal: false
            };
            setCriteria(filterData);
        }
    }, [selectedRequest]);

    const getTaggedUser = (name: string, users: UserDto[]) => {
        if (!_.isEmpty(name) && !_.isEmpty(users)) {
            return _.find(users, (item) => item.id === name);
        } else {
            return {
                id: '',
                displayName: '',
                email: ''
            };
        }
    };

    // add sas token to storage urls in comments
    const [parsedMessage, setParsedMessage] = useState<string>(commentsData?.items[0]?.statusText);
    useEffect(() => {
        if (commentsData?.items[0]?.statusText && commentsData?.items[0]?.statusText !== '') {
            const replaceStorageUrls = async () => {
                await addBlobFileSasToken(Entity.REQUEST, BlobStorageContainer.COMMENT_STATUS_FILES, commentsData?.items[0].statusText)
                .then((res) => {
                    if (res !== '') {
                        setParsedMessage(res);
                    }
                });
            }

            replaceStorageUrls();
        }
    }, [commentsData]);

    // Add SAS tokens only to storage URLs embedded in the description.
    const { data: selectedRequestDescription = selectedRequest?.description || '' } = useBlobFileSasTokens(
        Entity.REQUEST,
        BlobStorageContainer.REQUEST_DETAIL_FILES,
        selectedRequest?.description,
    );

    const generateCommentMessage = (message: string, taggedUsers: UserDto[]) => {
        let formattedMsg = message;
        if (message === '' || message === undefined) {
            return formattedMsg;
        }

        let users = Array.from(message?.matchAll(/<userid:(.*?)>/g));
        for (const user of users) {
            formattedMsg = formattedMsg.replace(
                user[0],
                `<span class="tagged-user">${getTaggedUser(user[1], taggedUsers)?.displayName}</span>`
            );
        }

        formattedMsg = formattedMsg.replaceAll('<:request-detail-url>', 'request');
        formattedMsg = ReplaceUrlByAnchor(injectTargetToAnchor(formattedMsg?.replace(/\\/g, '')), 'tagged-user', true);

        return formattedMsg;
    };

    const { mutate: associateRequests } = useAssociateRequests();
    const { mutate: postRequestTypeHelp } = usePostRequestType();

    // State variables for addCustomer
    const [requestProcessing, setRequestProcessing] = useState(false);
    const [displayScreen, setDisplayScreen] = useState('searchCust');
    const [editCustomer, setEditCustomer] = useState<IRemoveCustomerImpactData | undefined>(undefined);
    const [impactForRemoval, setImpactForRemoval] = useState<IRemoveCustomerImpactData>({});
    const [hideRemoveDialog, { setFalse: showRemoveDialog, setTrue: hideRemoveDialogAndReset }] = useBoolean(true);
    const [removeCustomerError, setRemoveCustomerError] = useState(false);
    const [toastCustomerName, setToastCustomerName] = useState('');
    const [showAddCustomerToast, setShowAddCustomerToast] = useState(false);
    const { mutate: removeCustomerImpactMutation, isLoading: isRemovingCustomer } = useRemoveCustomerImpact();

    useEffect(() => {
        if (displayScreen === 'successPopUp') {
            hideModal();
            setEditCustomer(undefined);
            setDisplayScreen('searchCust');
            setShowAddCustomerToast(true);
        }
    }, [displayScreen, hideModal]);

    const handleEditCustomer = (impact: IRemoveCustomerImpactData) => {
        setEditCustomer(impact);
        setDisplayScreen('addMultiDetails');
        setShowAddCustomerToast(false);
        showModal();
    };

    const dismissRemoveDialog = () => {
        setRemoveCustomerError(false);
        logFlowEvent(ViewTelemetryConstants.REMOVE_DIALOG_DISMISSED, VIEW_TELEMETRY_SECTION, {
            role: isRequestCreator ? 'creator' : 'viewer'
        });
        hideRemoveDialogAndReset();
    };

    const confirmRemoveCustomer = () => {
        if (isRemovingCustomer) {
            return;
        }
        const data: ICustomerImpactDto = {
            opportunityId: impactForRemoval.opportunityId,
            accountId: impactForRemoval.accountId,
            customerName: impactForRemoval.customerName
        };
        removeCustomerImpactMutation(
            {
                url: getURLForTeamAndType(selectedRequest.requestTeam, selectedRequest.requestType),
                id: selectedRequest.unifiedTrackingId,
                data
            },
            {
                onSuccess: () => {
                    setRemoveCustomerError(false);
                    logFlowEvent(ViewTelemetryConstants.CUSTOMER_REMOVED, VIEW_TELEMETRY_SECTION, {
                        role: 'creator',
                        requestType: selectedRequest.requestType
                    });
                    hideRemoveDialogAndReset();
                },
                onError: () => {
                    setRemoveCustomerError(true);
                    logFlowEvent(ViewTelemetryConstants.CUSTOMER_REMOVE_FAILED, VIEW_TELEMETRY_SECTION, {
                        role: 'creator',
                        requestType: selectedRequest.requestType
                    });
                }
            }
        );
    };

    const handleAddCustomerSuccess = (postAddCustomerResponseData: CustomerImpactResponseDto, addedCustomerImpacts: CustomerImpactDto[]) => {
        const successResolution = resolveCustomerImpactSuccess(
            postAddCustomerResponseData,
            selectedRequest.unifiedTrackingId
        );
        setToastCustomerName(addedCustomerImpacts[0]?.customerName || 'Customer');
        logFlowEvent(ViewTelemetryConstants.CUSTOMER_ADDED, VIEW_TELEMETRY_SECTION, {
            role: isRequestCreator ? 'creator' : 'viewer',
            requestType: selectedRequest.requestType,
            customerCount: addedCustomerImpacts.length,
            parentCreated: successResolution.parentCreated
        });
        if (!successResolution.matchesSelectedRequest) {
            setRequestProcessing(false);
            setDisplayScreen('errorPopUp');
            return;
        }

        if (successResolution.isStructuredFamilyResponse) {
            setRequestProcessing(false);
            setDisplayScreen('successPopUp');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries(StringConstants.REQUEST_QUERY);
            return;
        }

        if (selectedRequest.parentRequestId !== '' && selectedRequest.parentRequestId !== null) {
            const associationObject : IAssociateRequestsDto = {
                requestIds: [postAddCustomerResponseData?.id],
                associatedRequestId: selectedRequest.parentRequestId,
                expectedCustomerFamilySubmissionId: selectedRequest.customerFamilySubmissionId,
                expectedCustomerFamilyGeneration: selectedRequest.customerFamilyGeneration
            };

            associateRequests(associationObject, {
                onSuccess: () => {
                    setRequestProcessing(false);
                    setDisplayScreen('successPopUp');

                    AppQueryClient.invalidateQueries('getRequestById');
                    AppQueryClient.invalidateQueries('useRequestList');
                    AppQueryClient.invalidateQueries('requestQuery');
                },
                onError: () => {
                    setRequestProcessing(false);
                    setDisplayScreen('errorPopUp');
                }
            });
        } else if (selectedRequest.unifiedTrackingId !== postAddCustomerResponseData?.id) {
            // aggregate parent with selectedRequest becoming aggregate parent
            let datapass: IRequestDto = {
                summary: selectedRequest.summary,
                description: selectedRequest.description,

                requestedResolutionDate: selectedRequest.requestedResolutionDate,
                whyThisDateIsImportant: selectedRequest.whyThisDateIsImportant,

                status: 1,
                attachments: selectedRequest.attachments,
                customerImpacts: [] as CustomerImpactDto[],
                isParent: true
            };

            var aggregateCustomerImpact = [] as CustomerImpactDto[];

            selectedRequest.customerImpacts.forEach((impact) => {
                aggregateCustomerImpact.push(impact);
            });

            datapass.customerImpacts = aggregateCustomerImpact;
            datapass.status = selectedRequest.status;

            const requestAggregateData = requestAggregateSubmit(selectedRequest.requestType, selectedRequest, datapass);
            postRequestTypeHelp(
                { subUrl: requestAggregateData.subUrl, data: requestAggregateData.data },
                {
                    onSuccess: (response) => {
                        finishParentAssociation(response, postAddCustomerResponseData?.id);
                    }
                }
            );
        } else {
            setRequestProcessing(false);
            setDisplayScreen('successPopUp');

            AppQueryClient.invalidateQueries(StringConstants.REQUEST_QUERY);
        }
    };

    const finishParentAssociation = (parentId: string, postAddCustomerId: string) => {
        const associationObject = {
            requestIds: [selectedRequest.unifiedTrackingId, postAddCustomerId],
            associatedRequestId: parentId,
            expectedCustomerFamilySubmissionId: selectedRequest.customerFamilySubmissionId,
            expectedCustomerFamilyGeneration: selectedRequest.customerFamilyGeneration
        };

        associateRequests(associationObject, {
            onSuccess: () => {
                setRequestProcessing(false);
                setDisplayScreen('successPopUp');

                AppQueryClient.invalidateQueries('getRequestById');
                AppQueryClient.invalidateQueries('useRequestList');
                AppQueryClient.invalidateQueries('requestQuery');
            },
            onError: () => {
                setRequestProcessing(false);
                setDisplayScreen('errorPopUp');
            }
        });
    };

    useEffect(() => {
        if (requestData && requestData.length > 0 && selectedRequest && selectedRequest.unifiedTrackingId !== undefined) {
            let temp = requestData.filter((x) => x.unifiedTrackingId === selectedRequest.unifiedTrackingId)[0];
            if (setSelectedRequest) {
                setSelectedRequest(temp);
            }
        }
    }, [requestData]);

    useEffect(() => {
        const updateAttachmentCount = () => {
            setAttachmentCount(attachmentCounter(selectedRequest?.attachments, portal));
        };

        updateAttachmentCount();
    }, [selectedRequest]);

    return (
        <>
            <div className="details-panel-container" role="main" aria-label="details view panel">
                <div className="scroll-panel">
                    <div className="details-panel-wrapper">
                        {!isLoadingRequestList && selectedRequest && selectedRequest.unifiedTrackingId !== undefined && (
                            <>
                                <div className="dp-header">
                                    <Text className="dp-head-title">{selectedRequest.unifiedTrackingId}</Text>
                                    {renderTag(selectedRequest.status)}
                                    <div className="dp-head-desc">
                                        <Text className="title-field">{selectedRequest.summary}</Text>
                                    </div>
                                    <div className="dp-head-user">
                                        <Text>
                                            <Trans>Created by</Trans> :{' '}
                                            {selectedRequest.createdBy && selectedRequest.createdBy?.email}
                                        </Text>
                                        <Text className="dp-head-separator">|</Text>
                                        <Text>
                                            <Trans>Created On</Trans> :{' '}
                                            {ISOToDateTime(selectedRequest.createdOn?.toString(), 'MMM DD YYYY, hh:mm a')}
                                        </Text>
                                        {isGeneralFeedback && (
                                            <>
                                                <Text className="dp-head-separator">|</Text>
                                                <Text data-testid="general-feedback-header-label">
                                                    {GENERAL_FEEDBACK_CUSTOMER_LABEL}
                                                </Text>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div></div>
                                <div className="dp-filter-panel">
                                    <div className="dp-filter-links">
                                        <div className="dp-heading tab-heading">
                                            <Trans>Help Request Details</Trans>
                                        </div>
                                    </div>
                                    <div className="dp-filter-controls">
                                        <Link
                                            role="button"
                                            tabIndex={0}
                                            onClick={onToggleBookmark}
                                            data-testid="fav-test"
                                            className="filter-icon">
                                            <Icon
                                                title="Star icon"
                                                aria-label="starred"
                                                iconName={isStarred ? `FavoriteStarFill` : 'FavoriteStar'}
                                                className={`icon ${isStarred ? 'starred' : ''}`}
                                            />
                                        </Link>

                                        {selectedRequest?.status !== RequestStatus.Closed ||
                                        (selectedRequest?.status === RequestStatus.Closed &&
                                            selectedRequest?.createdBy?.id === userDetails?.id) ? (
                                            <Link
                                                role="button"
                                                className="attachments-icon-link filter-icon"
                                                tabIndex={0}
                                                onKeyPress={(e: React.KeyboardEvent<HTMLAnchorElement | HTMLElement | HTMLButtonElement | LinkBase>) => keyPress(e, handleAddAttachment)}
                                                onClick={handleAddAttachment}
                                                aria-label="attachments"
                                                data-testid="attachment-button">
                                                <ImageIcon
                                                    imageProps={{
                                                        src: attachmentActiveIcon,
                                                        height: 16,
                                                        width: 16
                                                    }}
                                                />
                                                <Text className="attachments-icon-count">{attachmentCount}</Text>
                                            </Link>
                                        ) : null}
                                        <Link
                                            role="button"
                                            tabIndex={0}
                                            onClick={handleShareRequest}
                                            data-testid="share-test"
                                            className="filter-icon">
                                            <Icon iconName="Share" className="icon" aria-label="share" />
                                        </Link>

                                        <DefaultButton
                                            disabled={selectedRequest?.isParent}
                                            className={
                                                !selectedRequest?.isParent ? 'default add-cust-btn' : 'disabled-add-cust-btn'
                                            }
                                            onClick={() => {
                                                addSuccessPopUp(false);
                                                setShowAddCustomerToast(false);
                                                setEditCustomer(undefined);
                                                setDisplayScreen('searchCust');
                                                showModal();
                                            }}
                                            data-testid="add-test">
                                            <Icon aria-hidden="true" iconName="Add" className="m-r-5" />
                                            <Text className='add-cust-label'>
                                                <Trans>Add a customer</Trans>
                                            </Text>
                                        </DefaultButton>
                                        {hideModal && (
                                            <AddCustomerSurface
                                                testId="details-add-customer"
                                                isEdit={!!editCustomer}
                                                isOpen={isModalOpen}
                                                onDismiss={() => {
                                                    setEditCustomer(undefined);
                                                    setDisplayScreen('searchCust');
                                                    hideModal();
                                                }}
                                                useDrawer={isNewTicketViewEnabled}
                                                enableEventBubbling>
                                                <AddImpact
                                                    selectedRequest={selectedRequest}
                                                    onDismiss={() => {
                                                        setEditCustomer(undefined);
                                                        setDisplayScreen('searchCust');
                                                        hideModal();
                                                    }}
                                                    globalSelectedRequest={selectedRequest}
                                                    setSelectedRequest={props.setSelectedRequest}
                                                    onSuccessHandler={handleAddCustomerSuccess}
                                                    requestProcessing={requestProcessing}
                                                    setRequestProcessing={setRequestProcessing}
                                                    displayScreen={displayScreen}
                                                    setDisplayScreen={setDisplayScreen}
                                                    initialCustomer={editCustomer}
                                                    onEditSuccess={() => {
                                                        setEditCustomer(undefined);
                                                        setDisplayScreen('searchCust');
                                                        hideModal();
                                                    }}
                                                />
                                            </AddCustomerSurface>
                                        )}
                                        {showAddCustomerToast && (
                                            <AddCustomerSuccessToast
                                                customerName={toastCustomerName}
                                                onDismiss={() => setShowAddCustomerToast(false)}
                                            />
                                        )}
                                    </div>
                                </div>
                                {!isLoadingComments && commentsData && commentsData.items?.length > 0 && (
                                    <div className="status-disp-container">
                                        <div className="status-disp-label">Status :</div>
                                        <div className="status-disp-desc">
                                            <div className="status-desc-text">
                                                <span
                                                    className="cmt-text status"
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(generateCommentMessage(
                                                            parsedMessage,
                                                            commentsData?.items[0].taggedUsers
                                                        ))
                                                    }}
                                                ></span>
                                            </div>
                                            <div className="status-desc-sub-text">
                                                {changeDateFormat(commentsData?.items[0]?.createdOn, 'dddd, MMM DD, YYYY')} at{' '}
                                                {changeDateFormat(commentsData?.items[0]?.createdOn, 'hh:mm a')}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="dp-content">
                                    <div className="dp-details">
                                        <DetailCard
                                            title={<Trans>Basic information</Trans>}
                                            useCardLayout={isNewTicketViewEnabled}>
                                        <table className="dp-content-details-tbl" role="presentation">
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <Trans>REQUEST TYPE</Trans>
                                                    </td>
                                                    <td>
                                                        <Trans>AGING</Trans>
                                                    </td>
                                                    {userDetails.email === selectedRequest.createdBy?.email && (
                                                        <td>
                                                            <Trans>CUSTOMER</Trans>
                                                        </td>
                                                    )}
                                                    <td>
                                                        <Trans>IMPACTED DEVICE</Trans>
                                                    </td>
                                                    <td>
                                                        <Trans>IMPACTED REVENUE</Trans>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>{GetTypes(selectedRequest.requestType)}</td>
                                                    <td>
                                                        <AgingProgress
                                                            startDate={selectedRequest?.createdOn}
                                                            endDate={selectedRequest?.requestedResolutionDate}
                                                            closedDate={selectedRequest.closedOn? selectedRequest.closedOn : selectedRequest?.requestedResolutionDate}
                                                        />
                                                    </td>
                                                    {userDetails.email === selectedRequest.createdBy?.email && (
                                                        <td data-testid="basic-info-customer">
                                                            {isNewTicketViewEnabled
                                                                ? isGeneralFeedback
                                                                    ? NO_CUSTOMER_PLACEHOLDER
                                                                    : resolveCustomerSummaryText(selectedRequest, {
                                                                          fallback: '--',
                                                                      })
                                                                : selectedRequest.customerImpacts?.length > 0
                                                                    ? formatCustomerSummary(
                                                                          selectedRequest.customerImpacts.map((customer) => ({
                                                                              accountName: customer.customerName
                                                                          }))
                                                                      )
                                                                    : '--'}
                                                        </td>
                                                    )}
                                                    <td data-testid="basic-info-msx-device">
                                                        {isGeneralFeedback
                                                            ? NO_CUSTOMER_PLACEHOLDER
                                                            : formatCurrency(selectedRequest.msxTotalDeviceCount)}
                                                    </td>
                                                    <td data-testid="basic-info-msx-revenue">
                                                        {isGeneralFeedback
                                                            ? NO_CUSTOMER_PLACEHOLDER
                                                            : `$ ${formatCurrency(selectedRequest.msxTotalRevenue)}`}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        </DetailCard>
                                        <DetailCard
                                            title={<Trans>Description</Trans>}
                                            useCardLayout={isNewTicketViewEnabled}
                                            legacyTitle={<Trans>DESCRIPTION</Trans>}>
                                        <div className="dp-content-text">
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(highlightTags(
                                                        ReplaceUrlByAnchor(
                                                            injectTargetToAnchor(selectedRequestDescription),
                                                            'link-item',
                                                            true
                                                        )
                                                    ))
                                                }}
                                            ></span>
                                        </div>
                                        </DetailCard>

                                        <ConfidentialIdentifier
                                            value={selectedRequest?.confidentialIdentifier}
                                            isVisible={showConfidentialIdentifier}
                                        />

                                        <ContextualDetailsCard
                                            selectedRequest={selectedRequest}
                                            viewerRole={viewerRole}
                                            isVisible={isNewTicketViewEnabled}
                                        />

                                        {isGeneralFeedback ? (
                                            <>
                                                <GeneralFeedbackEmptyState />
                                                <FeedbackSourceCard
                                                    selectedRequest={selectedRequest}
                                                    viewerRole={viewerRole}
                                                    isVisible={isNewTicketViewEnabled}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <div className="dp-content-title">
                                                    <Text>
                                                        {isRequestCreator ? (
                                                            <>Customers ({selectedRequest.customerImpacts?.length || 0})</>
                                                        ) : (
                                                            <Trans>Customer impact</Trans>
                                                        )}
                                                    </Text>
                                                </div>
                                                {isRequestCreator ? (
                                                    <CustomerDetailsTab
                                                        selectedRequest={selectedRequest}
                                                        toggleHideDialog={showRemoveDialog}
                                                        setImpactForRemoval={setImpactForRemoval}
                                                        companyInformation={selectedRequest.companyInformation || {}}
                                                        customerDetailsValidation={{
                                                            emailValidText: false,
                                                            contactNumberValidText: false,
                                                            alternateContactValidText: false
                                                        }}
                                                        setCustomerDetailsValidation={() => undefined}
                                                        countyCodePrefix={{ contactNumber: '', alternateContactNumber: '' }}
                                                        setCountryCodePrefix={() => undefined}
                                                        countryList={[]}
                                                        canManageCustomers
                                                        onAddOrEditCustomer={handleEditCustomer}
                                                    />
                                                ) : (
                                                    <div className="customer-impact-summary-card">
                                                        <div>
                                                            <Trans>Total impacted devices</Trans>
                                                            <strong>{formatCurrency(selectedRequest.msxTotalDeviceCount)}</strong>
                                                        </div>
                                                        <div>
                                                            <Trans>Total impacted revenue</Trans>
                                                            <strong>$ {formatCurrency(selectedRequest.msxTotalRevenue)}</strong>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {(selectedRequest.status !== RequestStatus.Closed ||
                                            selectedRequest.createdBy?.displayName === userDetails.displayName) && (
                                            <div>
                                                <div className="dp-content-title">
                                                    <Trans>COMMENTS</Trans>
                                                    {isClosed && <Trans>(CLOSED)</Trans>}:
                                                </div>
                                                <div>
                                                    {isShowComment && selectedRequest.unifiedTrackingId && (
                                                        <CommentSection
                                                            correlationId={selectedRequest.unifiedTrackingId}
                                                            isMultipleType={false}
                                                            defaultIsInternal={false}
                                                            isClosed={isClosed}
                                                            autoFocus={false}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        {isLoadingRequestList && <Loader classNames="m-t-20 m-b-50 height-70" />}
                        {!isLoadingRequestList && selectedRequest && selectedRequest.unifiedTrackingId === undefined && (
                            <div className="no-data">
                                <Trans>No data to display</Trans>
                            </div>
                        )}
                    </div>
                </div>

                <ShareRequestModal
                    modalState={isShareRequestModalOpen}
                    updateModalState={(value: boolean) => setIsShareRequestModalOpen(value)}
                    selectedRequest={selectedRequest}
                />
                <RemoveCustomerDialog
                    automationPrefix="creator"
                    hidden={hideRemoveDialog}
                    isRemoving={isRemovingCustomer}
                    customerName={impactForRemoval.customerName}
                    hasError={removeCustomerError}
                    onDismiss={dismissRemoveDialog}
                    onConfirm={confirmRemoveCustomer}
                />
                {isAddAttachmentModalOpen && (
                    <AddAttachmentModal
                        modalState={isAddAttachmentModalOpen}
                        updateModalState={(value: boolean) => setIsAddAttachmentModalOpen(value)}
                        selectedRequest={getModalData(selectedRequest)}
                        entityType={1}
                        onCompletedHandler={handleAttachmentCompleted}
                    />
                )}
            </div>
        </>
    );
};

export default DetailsPanel;
